import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Search, Download, RefreshCw, Filter, X, AlertTriangle, CheckSquare, Square, Package, Layers, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const PUT_AWAY_SOURCES = [
  ['pieces_processing', 'Pieces Processing'], ['common_pool', 'Common Pool'], ['sales_return', 'Sales Return'],
  ['rework_return', 'Rework Return'], ['resticker', 'Re-sticker'], ['fob_direct', 'FOB Direct'],
  ['cancelled', 'Cancelled Pick Slip'], ['not_recorded', 'Not recorded'],
];
const SOURCE_TONE = {
  pieces_processing: 'bg-emerald-100 text-emerald-800', common_pool: 'bg-sky-100 text-sky-800', sales_return: 'bg-amber-100 text-amber-800',
  rework_return: 'bg-orange-100 text-orange-800', resticker: 'bg-violet-100 text-violet-800', fob_direct: 'bg-teal-100 text-teal-800',
  cancelled: 'bg-red-100 text-red-800', not_recorded: 'bg-gray-100 text-gray-600',
};

const SourceSelect = ({ value, onChange, testId }) => (
  <div>
    <label className="text-sm font-medium text-gray-700 mb-1 block">Source</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2D5F4F]"
      data-testid={testId}
    >
      <option value="all">All Sources</option>
      {PUT_AWAY_SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);

const SourceBadges = ({ sources, testId }) => (
  <div className="flex flex-wrap gap-1" data-testid={testId}>
    {!sources?.length ? '-' : sources.map((s) => (
      <Badge key={`${s.key}-${s.label}`} className={SOURCE_TONE[s.key] || 'bg-gray-100 text-gray-700'}>
        {s.label}{sources.length > 1 ? ` · ${s.count}` : ''}
      </Badge>
    ))}
  </div>
);

const sourcesText = (sources) => (sources || []).map((s) => `${s.label} (${s.count})`).join('; ');


const PutAwayEntriesReport = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('entries');
  
  // Entries tab state
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedEntries, setSelectedEntries] = useState(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // By Bundle tab state
  const [bundles, setBundles] = useState([]);
  const [isLoadingBundles, setIsLoadingBundles] = useState(false);
  const [bundleCount, setBundleCount] = useState(0);
  const [bundleToDelete, setBundleToDelete] = useState(null);
  const [deleteBundleDialogOpen, setDeleteBundleDialogOpen] = useState(false);
  
  // By Design tab state
  const [designData, setDesignData] = useState([]);
  const [isLoadingDesign, setIsLoadingDesign] = useState(false);
  const [designCount, setDesignCount] = useState(0);
  const [designTotalQty, setDesignTotalQty] = useState(0);
  const [designFilters, setDesignFilters] = useState({
    barcode: '',
    design_name: '',
    date_from: '',
    date_to: '',
    source: 'all',  // all, GT, FG
    put_away_source: 'all'
  });
  const [isExportingDesign, setIsExportingDesign] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    slip_id: '',
    bin_code: '',
    design_name: '',
    barcode: '',
    date_from: '',
    date_to: '',
    status: 'all',  // all, STORED, PICKED, PACKED
    source: 'all'
  });
  const [bundleFilters, setBundleFilters] = useState({
    bundle_code: '',
    design_name: '',
    date_from: '',
    date_to: '',
    source: 'all'
  });
  const [showFilters, setShowFilters] = useState(true);

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.slip_id) params.append('slip_id', filters.slip_id);
      if (filters.bin_code) params.append('bin_code', filters.bin_code);
      if (filters.design_name) params.append('design_name', filters.design_name);
      if (filters.barcode) params.append('barcode', filters.barcode);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.source && filters.source !== 'all') params.append('source', filters.source);
      params.append('limit', '200');
      
      const response = await apiClient.get(`/wms/fg/put-away/put-away-entries?${params.toString()}`);
      
      if (response.data.success) {
        setEntries(response.data.entries || []);
        setTotalCount(response.data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Failed to load put away entries');
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  const fetchBundles = useCallback(async () => {
    setIsLoadingBundles(true);
    try {
      const params = new URLSearchParams();
      if (bundleFilters.bundle_code) params.append('bundle_code', bundleFilters.bundle_code);
      if (bundleFilters.design_name) params.append('design_name', bundleFilters.design_name);
      if (bundleFilters.date_from) params.append('date_from', bundleFilters.date_from);
      if (bundleFilters.date_to) params.append('date_to', bundleFilters.date_to);
      if (bundleFilters.source !== 'all') params.append('put_away_source', bundleFilters.source);
      params.append('limit', '100');
      
      const response = await apiClient.get(`/wms/fg/put-away/put-away-entries-by-bundle?${params.toString()}`);
      
      if (response.data.success) {
        setBundles(response.data.bundles || []);
        setBundleCount(response.data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching bundles:', error);
      toast.error('Failed to load bundle groups');
    } finally {
      setIsLoadingBundles(false);
    }
  }, [bundleFilters]);

  const fetchDesignData = useCallback(async () => {
    setIsLoadingDesign(true);
    try {
      const params = new URLSearchParams();
      if (designFilters.barcode) params.append('barcode', designFilters.barcode);
      if (designFilters.design_name) params.append('design_name', designFilters.design_name);
      if (designFilters.date_from) params.append('date_from', designFilters.date_from);
      if (designFilters.date_to) params.append('date_to', designFilters.date_to);
      if (designFilters.source && designFilters.source !== 'all') params.append('source', designFilters.source);
      if (designFilters.put_away_source !== 'all') params.append('put_away_source', designFilters.put_away_source);
      params.append('limit', '30000');
      
      const response = await apiClient.get(`/wms/fg/put-away/put-away-entries-by-design?${params.toString()}`);
      
      if (response.data.success) {
        setDesignData(response.data.data || []);
        setDesignCount(response.data.total || 0);
        setDesignTotalQty(response.data.total_qty || 0);
      }
    } catch (error) {
      console.error('Error fetching design data:', error);
      toast.error('Failed to load design-wise data');
    } finally {
      setIsLoadingDesign(false);
    }
  }, [designFilters]);

  useEffect(() => {
    if (activeTab === 'entries') {
      fetchEntries();
    } else if (activeTab === 'bundles') {
      fetchBundles();
    } else if (activeTab === 'design') {
      fetchDesignData();
    }
  }, [activeTab]);

  const handleSearch = () => {
    setSelectedEntries(new Set());
    if (activeTab === 'entries') {
      fetchEntries();
    } else if (activeTab === 'bundles') {
      fetchBundles();
    } else if (activeTab === 'design') {
      fetchDesignData();
    }
  };

  const handleClearFilters = () => {
    if (activeTab === 'entries') {
      setFilters({ slip_id: '', bin_code: '', design_name: '', barcode: '', date_from: '', date_to: '', status: 'all', source: 'all' });
    } else if (activeTab === 'bundles') {
      setBundleFilters({ bundle_code: '', design_name: '', date_from: '', date_to: '', source: 'all' });
    } else if (activeTab === 'design') {
      setDesignFilters({ barcode: '', design_name: '', date_from: '', date_to: '', source: 'all', put_away_source: 'all' });
    }
  };

  const handleSelectEntry = (entryKey) => {
    setSelectedEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryKey)) {
        newSet.delete(entryKey);
      } else {
        newSet.add(entryKey);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedEntries.size === entries.length) {
      setSelectedEntries(new Set());
    } else {
      setSelectedEntries(new Set(entries.map(e => e.fg_inv_id || e.usn_code)));
    }
  };

  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry);
    setDeleteDialogOpen(true);
  };

  const handleBulkDeleteClick = () => {
    if (selectedEntries.size === 0) {
      toast.error('No entries selected');
      return;
    }
    setEntryToDelete(null);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      if (entryToDelete) {
        const entryKey = entryToDelete.fg_inv_id || entryToDelete.usn_code;
        await apiClient.delete(`/wms/fg/put-away/put-away-entries/${entryKey}`);
        toast.success('Entry deleted successfully');
      } else {
        // Separate selected entries into those with fg_inv_id and those with only usn_code
        const selectedArr = Array.from(selectedEntries);
        const fg_inv_ids = [];
        const usn_codes = [];
        for (const key of selectedArr) {
          const entry = entries.find(e => (e.fg_inv_id || e.usn_code) === key);
          if (entry?.fg_inv_id) {
            fg_inv_ids.push(entry.fg_inv_id);
          } else {
            usn_codes.push(key);
          }
        }
        await apiClient.delete('/wms/fg/put-away/put-away-entries/bulk', {
          data: { fg_inv_ids, usn_codes }
        });
        toast.success(`Deleted ${selectedEntries.size} entries successfully`);
        setSelectedEntries(new Set());
      }
      setDeleteDialogOpen(false);
      fetchEntries();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete entries');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteBundleClick = (bundle) => {
    setBundleToDelete(bundle);
    setDeleteBundleDialogOpen(true);
  };

  const handleConfirmBundleDelete = async () => {
    if (!bundleToDelete) return;
    
    setIsDeleting(true);
    try {
      const response = await apiClient.delete(`/wms/fg/put-away/put-away-entries/bundle/${encodeURIComponent(bundleToDelete.bundle_code)}`);
      toast.success(response.data.message || `Deleted ${bundleToDelete.usn_count} entries for bundle ${bundleToDelete.bundle_code}`);
      setDeleteBundleDialogOpen(false);
      setBundleToDelete(null);
      fetchBundles();
    } catch (error) {
      console.error('Error deleting bundle:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete bundle entries');
    } finally {
      setIsDeleting(false);
    }
  };

  // ----- Admin: dedupe duplicate FG inventory rows + force-reverse wrong put-away -----
  const [dedupeRunning, setDedupeRunning] = useState(false);
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
  const [reverseUsn, setReverseUsn] = useState('');
  const [reverseReason, setReverseReason] = useState('Wrongly re-put-away after picking');
  const [reverseRunning, setReverseRunning] = useState(false);

  const handleDedupeStorageRows = async (dryRun) => {
    setDedupeRunning(true);
    try {
      const resp = await apiClient.post(`/wms/fg/put-away/dedupe-storage-rows?dry_run=${dryRun ? 'true' : 'false'}`);
      const d = resp.data || {};
      const verb = dryRun ? 'Would archive' : 'Archived';
      toast.success(
        `${verb} ${d.rows_archived || 0} duplicate row(s) across ${d.duplicated_usn_count || 0} USN(s).` +
        (d.rows_backfilled_with_fg_inv_id ? ` Backfilled fg_inv_id on ${d.rows_backfilled_with_fg_inv_id} legacy row(s).` : ''),
        { duration: 7000 }
      );
      if (!dryRun) fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dedupe failed');
    } finally {
      setDedupeRunning(false);
    }
  };

  const handleForceReverse = async () => {
    const usn = (reverseUsn || '').trim();
    if (!usn) {
      toast.error('Enter the USN to force-reverse');
      return;
    }
    setReverseRunning(true);
    try {
      const resp = await apiClient.post(
        '/wms/fg/put-away/reverse-wrong-putaway',
        { usn_code: usn, reason: reverseReason || 'wrong_re_putaway' }
      );
      const d = resp.data || {};
      toast.success(d.message || `USN ${usn} flipped back to PICKED`);
      setReverseDialogOpen(false);
      setReverseUsn('');
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Force-reverse failed');
    } finally {
      setReverseRunning(false);
    }
  };

  const handleExportCSV = () => {
    if (activeTab === 'entries') {
      if (entries.length === 0) {
        toast.error('No data to export');
        return;
      }
      
      const headers = ['Entry ID', 'USN Code', 'Bin Code', 'Barcode', 'Design Name', 'Size', 'Shade', 'Slip ID', 'Source', 'Put Away Date', 'Put Away By', 'Status'];
      const csvContent = [
        headers.join(','),
        ...entries.map(item => [
          item.fg_inv_id || item.usn_code,
          item.usn_code,
          item.bin_code,
          item.item_barcode,
          `"${item.design_name}"`,
          item.size,
          item.shade,
          item.put_away_slip_id,
          `"${[item.put_away_source_label, ...(item.put_away_source_tags || [])].filter(Boolean).join(' · ')}"`,
          item.put_away_date,
          `"${item.put_away_by_name || item.put_away_by || ''}"`,
          item.status
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `put_away_entries_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    } else if (activeTab === 'bundles') {
      if (bundles.length === 0) {
        toast.error('No data to export');
        return;
      }
      
      const headers = ['Bundle Code', 'Design Name', 'Shade', 'Size', 'Barcode', 'USN Count', 'Source', 'Bin Codes', 'First Put Away', 'Last Put Away'];
      const csvContent = [
        headers.join(','),
        ...bundles.map(item => [
          item.bundle_code,
          `"${item.design_name}"`,
          item.shade,
          item.size,
          item.item_barcode,
          item.usn_count,
          `"${sourcesText(item.sources)}"`,
          `"${(item.bin_codes || []).join('; ')}"`,
          item.first_put_away,
          item.last_put_away
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `put_away_by_bundle_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    } else if (activeTab === 'design') {
      if (designData.length === 0) {
        toast.error('No data to export');
        return;
      }
      
      const headers = ['Barcode', 'Design Name', 'Qty', 'Source', 'Put Away Timestamp'];
      const csvContent = [
        headers.join(','),
        ...designData.map(item => [
          item.barcode,
          `"${item.design_name || ''}"`,
          item.qty,
          `"${item.put_away_source_label || ''}"`,
          item.put_away_date
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `put_away_by_design_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    }
    toast.success('Report exported successfully');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/fg')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Put Away Entries</h1>
              <p className="text-sm text-[#E8DCC8]">Admin - View & Delete Entries</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {activeTab === 'entries' && selectedEntries.size > 0 && (
              <Button
                variant="destructive"
                onClick={handleBulkDeleteClick}
                className="bg-red-600 hover:bg-red-700"
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete ({selectedEntries.size})
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => handleDedupeStorageRows(true)}
              disabled={dedupeRunning}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              data-testid="dedupe-dry-run-btn"
              title="Preview duplicate FG inventory rows that would be cleaned up"
            >
              <Layers className="w-4 h-4 mr-2" />
              {dedupeRunning ? 'Working…' : 'Dedupe (Dry-run)'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (window.confirm('Archive every duplicate FG inventory row and keep only the canonical one? This is irreversible.')) {
                  handleDedupeStorageRows(false);
                }
              }}
              disabled={dedupeRunning}
              className="bg-amber-500/20 border-amber-300/40 text-white hover:bg-amber-500/30"
              data-testid="dedupe-run-btn"
              title="Permanently archive duplicate FG inventory rows"
            >
              <Layers className="w-4 h-4 mr-2" />
              Run Dedupe
            </Button>
            <Button
              variant="outline"
              onClick={() => setReverseDialogOpen(true)}
              className="bg-orange-500/20 border-orange-300/40 text-white hover:bg-orange-500/30"
              data-testid="force-reverse-btn"
              title="Flip a wrongly re-put-away USN back to PICKED so it can ship"
            >
              <Undo2 className="w-4 h-4 mr-2" />
              Force-Reverse
            </Button>
            <Button
              variant="outline"
              onClick={handleExportCSV}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="entries" data-testid="tab-entries">By Entry</TabsTrigger>
            <TabsTrigger value="bundles" data-testid="tab-bundles">
              <Package className="w-4 h-4 mr-2" />
              By Bundle
            </TabsTrigger>
            <TabsTrigger value="design" data-testid="tab-design">
              By Design
            </TabsTrigger>
          </TabsList>
          
          {/* BY ENTRY TAB */}
          <TabsContent value="entries" className="space-y-4">
            {/* Stats */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Badge variant="outline" className="text-lg py-1 px-3">
                  Total: {totalCount} entries
                </Badge>
                {selectedEntries.size > 0 && (
                  <Badge className="bg-blue-100 text-blue-800 text-lg py-1 px-3">
                    Selected: {selectedEntries.size}
                  </Badge>
                )}
              </div>
            </div>

            {/* Entry Filters */}
            <Card>
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    Filters
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      {showFilters ? 'Hide' : 'Show'}
                    </Button>
                    <Button
                      onClick={handleSearch}
                      size="sm"
                      className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                      disabled={isLoading}
                      data-testid="search-btn"
                    >
                      {isLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                      Search
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {showFilters && (
                <CardContent className="pt-0 pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Slip ID</label>
                      <Input
                        value={filters.slip_id}
                        onChange={(e) => setFilters(prev => ({ ...prev, slip_id: e.target.value }))}
                        placeholder="PAS-GT-..."
                        data-testid="filter-slip-id"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Bin Code</label>
                      <Input
                        value={filters.bin_code}
                        onChange={(e) => setFilters(prev => ({ ...prev, bin_code: e.target.value }))}
                        placeholder="H02-A-01"
                        data-testid="filter-bin-code"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Design Name</label>
                      <Input
                        value={filters.design_name}
                        onChange={(e) => setFilters(prev => ({ ...prev, design_name: e.target.value }))}
                        placeholder="Omkar"
                        data-testid="filter-design"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Barcode/USN</label>
                      <Input
                        value={filters.barcode}
                        onChange={(e) => setFilters(prev => ({ ...prev, barcode: e.target.value }))}
                        placeholder="MW15914"
                        data-testid="filter-barcode"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Date From</label>
                      <Input
                        type="date"
                        value={filters.date_from}
                        onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                        data-testid="filter-date-from"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Date To</label>
                      <Input
                        type="date"
                        value={filters.date_to}
                        onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                        data-testid="filter-date-to"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Status</label>
                      <select
                        value={filters.status}
                        onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                        className="w-full h-10 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2D5F4F]"
                        data-testid="filter-status"
                      >
                        <option value="all">All Status</option>
                        <option value="STORED">Stored (In Bin)</option>
                        <option value="PICKED">Picked</option>
                        <option value="PACKED">Packed</option>
                        <option value="CANCELLED">Cancelled</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Source</label>
                      <select
                        value={filters.source}
                        onChange={(e) => setFilters(prev => ({ ...prev, source: e.target.value }))}
                        className="w-full h-10 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2D5F4F]"
                        data-testid="filter-source"
                      >
                        <option value="all">All Sources</option>
                        {PUT_AWAY_SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                      <X className="w-4 h-4 mr-2" />
                      Clear Filters
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Entries Table */}
            <Card>
              <CardContent className="p-0">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                  </div>
                ) : entries.length === 0 ? (
                  <div className="text-center py-12">
                    <AlertTriangle className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No entries found</p>
                    <p className="text-sm text-gray-400">Try adjusting your filters</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="w-10">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleSelectAll}
                              className="p-0 h-6 w-6"
                            >
                              {selectedEntries.size === entries.length ? (
                                <CheckSquare className="w-5 h-5 text-blue-600" />
                              ) : (
                                <Square className="w-5 h-5" />
                              )}
                            </Button>
                          </TableHead>
                          <TableHead className="font-semibold">Entry ID</TableHead>
                          <TableHead className="font-semibold">USN Code</TableHead>
                          <TableHead className="font-semibold">Bin Code</TableHead>
                          <TableHead className="font-semibold">Status</TableHead>
                          <TableHead className="font-semibold">Barcode</TableHead>
                          <TableHead className="font-semibold">Design Name</TableHead>
                          <TableHead className="font-semibold">Slip ID</TableHead>
                          <TableHead className="font-semibold">Source</TableHead>
                          <TableHead className="font-semibold">Put Away Date</TableHead>
                          <TableHead className="font-semibold">Put Away By</TableHead>
                          <TableHead className="font-semibold text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {entries.map((entry) => {
                          const entryKey = entry.fg_inv_id || entry.usn_code;
                          return (
                          <TableRow 
                            key={entryKey} 
                            className={`hover:bg-gray-50 ${selectedEntries.has(entryKey) ? 'bg-blue-50' : ''}`}
                          >
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSelectEntry(entryKey)}
                                className="p-0 h-6 w-6"
                              >
                                {selectedEntries.has(entryKey) ? (
                                  <CheckSquare className="w-5 h-5 text-blue-600" />
                                ) : (
                                  <Square className="w-5 h-5" />
                                )}
                              </Button>
                            </TableCell>
                            <TableCell className="font-mono text-xs">{entry.fg_inv_id || '-'}</TableCell>
                            <TableCell className="font-mono text-sm">{entry.usn_code}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="font-mono">
                                {entry.bin_code}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                className={
                                  entry.status === 'STORED' ? 'bg-green-100 text-green-800' :
                                  entry.status === 'PICKED' ? 'bg-blue-100 text-blue-800' :
                                  entry.status === 'PACKED' ? 'bg-purple-100 text-purple-800' :
                                  entry.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                                  entry.is_deleted ? 'bg-gray-100 text-gray-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }
                              >
                                {entry.status || 'Unknown'}
                                {entry.is_deleted && ' (Deleted)'}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-sm">{entry.item_barcode}</TableCell>
                            <TableCell className="font-medium">{entry.design_name}</TableCell>
                            <TableCell className="font-mono text-xs">{entry.put_away_slip_id || '-'}</TableCell>
                            <TableCell data-testid={`put-away-source-${entryKey}`}>
                              <Badge className={SOURCE_TONE[entry.put_away_source] || 'bg-gray-100 text-gray-700'}>{entry.put_away_source_label || '-'}</Badge>
                              {(entry.put_away_source_tags || []).map((t) => <div key={t} className="text-[10px] text-gray-500 mt-0.5">{t}</div>)}
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {formatDate(entry.put_away_date)}
                            </TableCell>
                            <TableCell className="text-sm text-gray-700" data-testid={`put-away-by-${entryKey}`}>
                              {entry.put_away_by_name || entry.put_away_by || '-'}
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteClick(entry)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-btn-${entryKey}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* BY BUNDLE TAB */}
          <TabsContent value="bundles" className="space-y-4">
            {/* Stats */}
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="text-lg py-1 px-3">
                Total: {bundleCount} bundles
              </Badge>
            </div>

            {/* Bundle Filters */}
            <Card>
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    Filters
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      {showFilters ? 'Hide' : 'Show'}
                    </Button>
                    <Button
                      onClick={handleSearch}
                      size="sm"
                      className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                      disabled={isLoadingBundles}
                      data-testid="search-bundles-btn"
                    >
                      {isLoadingBundles ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                      Search
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {showFilters && (
                <CardContent className="pt-0 pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Bundle Code</label>
                      <Input
                        value={bundleFilters.bundle_code}
                        onChange={(e) => setBundleFilters(prev => ({ ...prev, bundle_code: e.target.value }))}
                        placeholder="BUN..."
                        data-testid="filter-bundle-code"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Design Name</label>
                      <Input
                        value={bundleFilters.design_name}
                        onChange={(e) => setBundleFilters(prev => ({ ...prev, design_name: e.target.value }))}
                        placeholder="Omkar"
                        data-testid="filter-bundle-design"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Date From</label>
                      <Input
                        type="date"
                        value={bundleFilters.date_from}
                        onChange={(e) => setBundleFilters(prev => ({ ...prev, date_from: e.target.value }))}
                        data-testid="filter-bundle-date-from"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Date To</label>
                      <Input
                        type="date"
                        value={bundleFilters.date_to}
                        onChange={(e) => setBundleFilters(prev => ({ ...prev, date_to: e.target.value }))}
                        data-testid="filter-bundle-date-to"
                      />
                    </div>
                    <SourceSelect value={bundleFilters.source} onChange={(v) => setBundleFilters(prev => ({ ...prev, source: v }))} testId="filter-bundle-source" />
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                      <X className="w-4 h-4 mr-2" />
                      Clear Filters
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Bundles Table */}
            <Card>
              <CardContent className="p-0">
                {isLoadingBundles ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                  </div>
                ) : bundles.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No bundles found</p>
                    <p className="text-sm text-gray-400">Try adjusting your filters</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="font-semibold">Bundle Code</TableHead>
                          <TableHead className="font-semibold">Design Name</TableHead>
                          <TableHead className="font-semibold">Shade</TableHead>
                          <TableHead className="font-semibold">Size</TableHead>
                          <TableHead className="font-semibold">Barcode</TableHead>
                          <TableHead className="font-semibold text-center">USN Count</TableHead>
                          <TableHead className="font-semibold">Source</TableHead>
                          <TableHead className="font-semibold">Bin Locations</TableHead>
                          <TableHead className="font-semibold">Last Put Away</TableHead>
                          <TableHead className="font-semibold text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bundles.map((bundle) => (
                          <TableRow key={bundle.bundle_code} className="hover:bg-gray-50">
                            <TableCell className="font-mono font-bold text-purple-700">{bundle.bundle_code}</TableCell>
                            <TableCell className="font-medium">{bundle.design_name}</TableCell>
                            <TableCell>{bundle.shade || '-'}</TableCell>
                            <TableCell>{bundle.size || '-'}</TableCell>
                            <TableCell className="font-mono text-sm">{bundle.item_barcode || '-'}</TableCell>
                            <TableCell className="text-center">
                              <Badge className="bg-blue-100 text-blue-800">{bundle.usn_count}</Badge>
                            </TableCell>
                            <TableCell><SourceBadges sources={bundle.sources} testId={`bundle-source-${bundle.bundle_code}`} /></TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {(bundle.bin_codes || []).slice(0, 3).map((bin, idx) => (
                                  <Badge key={idx} variant="outline" className="font-mono text-xs">{bin}</Badge>
                                ))}
                                {(bundle.bin_codes || []).length > 3 && (
                                  <Badge variant="outline" className="text-xs">+{bundle.bin_codes.length - 3} more</Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {formatDate(bundle.last_put_away)}
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteBundleClick(bundle)}
                                className="bg-red-600 hover:bg-red-700"
                                data-testid={`delete-bundle-btn-${bundle.bundle_code}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete All
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* BY DESIGN TAB */}
          <TabsContent value="design" className="space-y-4">
            {/* Stats */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Badge variant="outline" className="text-lg py-1 px-3">
                  Total: {designCount} records
                </Badge>
                <Badge className="bg-green-100 text-green-800 text-lg py-1 px-3">
                  Total Qty: {designTotalQty.toLocaleString()}
                </Badge>
              </div>
            </div>

            {/* Design Filters */}
            <Card>
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    Filters
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      {showFilters ? 'Hide' : 'Show'}
                    </Button>
                    <Button
                      onClick={handleSearch}
                      size="sm"
                      className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                      disabled={isLoadingDesign}
                      data-testid="search-design-btn"
                    >
                      {isLoadingDesign ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                      Search
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {showFilters && (
                <CardContent className="pt-0 pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Put Away Type</label>
                      <Select
                        value={designFilters.source}
                        onValueChange={(value) => setDesignFilters(prev => ({ ...prev, source: value }))}
                      >
                        <SelectTrigger data-testid="filter-design-source">
                          <SelectValue placeholder="All Sources" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Sources</SelectItem>
                          <SelectItem value="GT">GT Put Away</SelectItem>
                          <SelectItem value="FG">FG Put Away</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Barcode</label>
                      <Input
                        value={designFilters.barcode}
                        onChange={(e) => setDesignFilters(prev => ({ ...prev, barcode: e.target.value }))}
                        placeholder="MW..."
                        data-testid="filter-design-barcode"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Design Name</label>
                      <Input
                        value={designFilters.design_name}
                        onChange={(e) => setDesignFilters(prev => ({ ...prev, design_name: e.target.value }))}
                        placeholder="Omkar"
                        data-testid="filter-design-name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Date From</label>
                      <Input
                        type="date"
                        value={designFilters.date_from}
                        onChange={(e) => setDesignFilters(prev => ({ ...prev, date_from: e.target.value }))}
                        data-testid="filter-design-date-from"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Date To</label>
                      <Input
                        type="date"
                        value={designFilters.date_to}
                        onChange={(e) => setDesignFilters(prev => ({ ...prev, date_to: e.target.value }))}
                        data-testid="filter-design-date-to"
                      />
                    </div>
                    <SourceSelect value={designFilters.put_away_source} onChange={(v) => setDesignFilters(prev => ({ ...prev, put_away_source: v }))} testId="filter-design-put-away-source" />
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                      <X className="w-4 h-4 mr-2" />
                      Clear Filters
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Design Data Table */}
            <Card>
              <CardContent className="p-0">
                {isLoadingDesign ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                  </div>
                ) : designData.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No design data found</p>
                    <p className="text-sm text-gray-400">Try adjusting your filters</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="font-semibold">Barcode</TableHead>
                          <TableHead className="font-semibold">Design Name</TableHead>
                          <TableHead className="font-semibold text-center">Qty</TableHead>
                          <TableHead className="font-semibold">Source</TableHead>
                          <TableHead className="font-semibold">Put Away Timestamp</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {designData.map((item, index) => (
                          <TableRow key={`${item.barcode}-${item.put_away_date}-${index}`} className="hover:bg-gray-50">
                            <TableCell className="font-mono font-bold text-blue-700">{item.barcode}</TableCell>
                            <TableCell className="font-medium">{item.design_name}</TableCell>
                            <TableCell className="text-center">
                              <Badge className="bg-blue-100 text-blue-800">{item.qty}</Badge>
                            </TableCell>
                            <TableCell data-testid={`design-source-${index}`}>
                              <Badge className={SOURCE_TONE[item.put_away_source] || 'bg-gray-100 text-gray-700'}>{item.put_away_source_label || '-'}</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {formatDate(item.put_away_date)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Delete Entry Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirm Delete
            </DialogTitle>
            <DialogDescription>
              {entryToDelete ? (
                <>
                  Are you sure you want to delete this entry?
                  <div className="mt-2 p-3 bg-gray-50 rounded text-sm">
                    <p><strong>USN:</strong> {entryToDelete.usn_code}</p>
                    <p><strong>Bin:</strong> {entryToDelete.bin_code}</p>
                    <p><strong>Design:</strong> {entryToDelete.design_name}</p>
                  </div>
                </>
              ) : (
                <>
                  Are you sure you want to delete <strong>{selectedEntries.size}</strong> selected entries?
                  <p className="mt-2 text-red-600">This action cannot be undone.</p>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              data-testid="confirm-delete-btn"
            >
              {isDeleting ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Bundle Confirmation Dialog */}
      <Dialog open={deleteBundleDialogOpen} onOpenChange={setDeleteBundleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete All Bundle Entries
            </DialogTitle>
            <DialogDescription>
              {bundleToDelete && (
                <>
                  <p className="mb-3">Are you sure you want to delete <strong>ALL</strong> put away entries for this bundle?</p>
                  <div className="p-3 bg-gray-50 rounded text-sm space-y-1">
                    <p><strong>Bundle Code:</strong> <span className="font-mono text-purple-700">{bundleToDelete.bundle_code}</span></p>
                    <p><strong>Design:</strong> {bundleToDelete.design_name}</p>
                    <p><strong>USN Count:</strong> <span className="font-bold text-red-600">{bundleToDelete.usn_count} entries will be deleted</span></p>
                    <p><strong>Bin Locations:</strong> {(bundleToDelete.bin_codes || []).join(', ') || '-'}</p>
                  </div>
                  <p className="mt-3 text-red-600 font-medium">This action cannot be undone. All entries regardless of status will be permanently deleted.</p>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteBundleDialogOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleConfirmBundleDelete}
              disabled={isDeleting}
              data-testid="confirm-bundle-delete-btn"
            >
              {isDeleting ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              {isDeleting ? 'Deleting...' : 'Delete All Entries'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force-Reverse wrongly re-put-away USN */}
      <Dialog open={reverseDialogOpen} onOpenChange={setReverseDialogOpen}>
        <DialogContent data-testid="force-reverse-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Undo2 className="w-5 h-5 text-orange-600" />
              Force-Reverse Wrong Put-Away
            </DialogTitle>
            <DialogDescription>
              Use this when staff scanned a PICKED USN at FG Put-Away or Table
              Clearance by mistake and it landed back in storage instead of
              going to Packing. This flips the row back to <b>PICKED</b> and
              clears the bin so the USN can ship.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">USN code</label>
              <Input
                value={reverseUsn}
                onChange={(e) => setReverseUsn(e.target.value)}
                placeholder="Scan or paste the wrongly re-put-away USN"
                data-testid="force-reverse-usn-input"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">Reason / note</label>
              <Input
                value={reverseReason}
                onChange={(e) => setReverseReason(e.target.value)}
                data-testid="force-reverse-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReverseDialogOpen(false)}
              data-testid="force-reverse-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleForceReverse}
              disabled={reverseRunning || !reverseUsn.trim()}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="force-reverse-confirm-btn"
            >
              <Undo2 className="w-4 h-4 mr-2" />
              {reverseRunning ? 'Reversing…' : 'Flip back to PICKED'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PutAwayEntriesReport;
